import React from "react";
import classNames from "classnames";
import uniqueId from "lodash.uniqueid";

import { InputProps } from "./Input.types";
import "./Input.scss";
import InputIcon from "./InputIcon";
import InputLabel from "./InputLabel";
import InputErrorLabel from "./InputErrorLabel";

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hideLabel,
      errorLabel,
      size,
      invalid,
      className,
      disabled,
      icon,
      onIconClick,
      required,
      ...props
    },
    ref
  ) => {
    const additionalProps = { ...props };
    const borderClassNames = classNames(
      "elemental-Input-border",
      {
        "elemental-Input-border--withIconButton":
          !!onIconClick && (!!size || size === "medium"),
        "elemental-Input-border--large": !onIconClick && size === "large",
        "elemental-Input-border--small": !onIconClick && size === "small",
        "elemental-Input-border--large-withIconButton":
          !!onIconClick && size === "large",
        "elemental-Input-border--small-withIconButton":
          !!onIconClick && size === "small",
        "elemental-Input-border--invalid": invalid,
        "elemental-Input-border--disabled": disabled,
      },
      className
    );

    const inputId = uniqueId("input_");
    const inputProps = {
      id: inputId,
      className: "elemental-Input-input",
      disabled,
      "aria-invalid": invalid,
      "aria-required": required,
    };
    const labelProps = { label, hideLabel, required, inputId };
    const iconProps = { disabled, icon, onIconClick, size, invalid };

    let errorLabelId = null;
    if (errorLabel && invalid) {
      errorLabelId = uniqueId("input_error_");
      additionalProps["aria-describedby"] = errorLabelId;
    }
    const errorLabelProps = { id: errorLabelId, errorLabel, invalid };

    return (
      <div className="elemental-Input" data-testid="input-component" ref={ref}>
        <InputLabel {...labelProps} />
        <div className={borderClassNames} data-testid="input-component-border">
          <input
            {...inputProps}
            {...additionalProps}
            data-testid="input-component-input"
          />
          <InputIcon {...iconProps} />
        </div>
        <InputErrorLabel {...errorLabelProps} />
      </div>
    );
  }
);

export default Input;
